import PropTypes from 'prop-types';
import React from 'react';
import { css } from 'emotion';
import Flexbox from 'flexbox-react';
import ProcessDataTable from 'components/core/ProcessDataTable';
import Toolbar from 'components/core/toolbar/Toolbar';
import ToolbarItem from 'components/core/toolbar/ToolbarItem';
import RIEInput from 'components/core/editable/RIEInput';
import InfoIcon from 'components/core/InfoIcon';
import SimpleMessage from 'components/core/help/SimpleMessage';
import ProcessUtils from 'utils/ProcessUtils';
import ComponentUtils from 'utils/ComponentUtils';
import ProcessValidator from 'utils/ProcessValidator';
import Constants from 'utils/Constants';

const qcHelpMessageStyles = css`
    list-style-type:none;
    padding-left:0;
    td {
        vertical-align:top
    }
    .qc-number {
        font-weight:bold;
        padding-right: 5px;
    }
`;

export default class OverrideTable extends ProcessDataTable {
    static propTypes = {
        hasWritePermissions: PropTypes.bool,
        displayInputDatastream: PropTypes.bool,
        parameterNames: PropTypes.array.isRequired,
        coordinateSystemName: PropTypes.string,
        dimensionName: PropTypes.string
    };

    static defaultProps = {
        displayInputDatastream: false,
        selectable: true,
        className: css`width:100%;`
    };

    getTableRowData = (props) => {
        const coordinateSystemName = props.coordinateSystemName ? props.coordinateSystemName : null;
        const dimensionName = props.dimensionName ? props.dimensionName : null;

        //go through parameter list and combine variable data
        let variables = {};
        props.parameterNames.forEach(parameterName => {
            ProcessUtils.visitTransformationVariables(props.process, parameterName, (variableObj) => {

                // for assigning bad values, if there are no indeterminate qc there is nothing additional to add to bad value list, shouldn't include
                let includeVariable = true;
                const variableCoordinateSystem = ProcessUtils.getTransformVariableCoordinateSystem(props.process, parameterName, variableObj.name);

                if (includeVariable && variableCoordinateSystem === coordinateSystemName && variableObj.dim === dimensionName) {
                    let key = variableObj.name;
                    if (variableObj.input_datastream) {
                        key = `${variableObj.input_datastream}-${variableObj.name}`;
                    }
                    const data = Object.assign({}, variables[key], { [parameterName]: variableObj.value });
                    variables = Object.assign({}, variables, { [key]: data });
                }
            });
        });

        const rows = [];
        Object.keys(variables).forEach(key => {
            const retrievedVariable = this.getRetrievedVariableFromKey(key);
            const inputDatastream =  this.getDatastreamFromKey(key);
            const rowData = {
                key,
                retrievedVariable,
                inputDatastream,
                data: { retrievedVariable, inputDatastream, value: variables[key] }
            };

            rows.push(rowData);
        });

        return rows;

    };

    getRowKey() {
        return 'key';
    }

    getDatastreamFromKey = (key) => {
        let datastreamName = null;
        if (key.indexOf('-') > -1) {
            const data = key.split('-');
            datastreamName = data[0];
        }

        return datastreamName;
    };

    getRetrievedVariableFromKey = (key) => {
        let retrievedVariableName = key;
        if (key.indexOf('-') > -1) {
            const data = key.split('-');
            retrievedVariableName = data[1];
        }

        return retrievedVariableName;
    };

    getValue = (data, parameterName = null) => {
        // only used if table has single parameter
        if (!parameterName && this.props.parameterNames.length === 1) {
            parameterName = this.props.parameterNames[0];
        }
        return data.value[parameterName] ? data.value[parameterName] : '';
    };

    getFieldPath = (parameterName, data) => {
        return ProcessUtils.getTransformOverrideVariableFieldPath(parameterName, this.props.coordinateSystemName,
            this.props.dimensionName, data.inputDatastream, data.retrievedVariable);
    };

    getVariableOptions = () => {
        let variableOptions = [];

        ProcessUtils.visitRetrievedVariables(this.props.process, (retrievedVariable) => {
            if (retrievedVariable.coordinate_system === this.props.coordinateSystemName) {

                // Not only must the variable be in the coordinate system, but it must also have the
                // current dimension to be included in the list
                if (retrievedVariable.dims.indexOf(this.props.dimensionName) >= 0) {
                    // We also must check if the variable is currently set to PASSTHROUGH for this dimension.
                    // If it is, then all transform parameters except transform type should be excluded since no
                    // transformation will occur.
                    const variableTType = ProcessUtils.getVariableTransformationType(this.props.process, retrievedVariable.name, this.props.dimensionName)
                    if (this.props.parameterNames[0] === 'transformation_type' || variableTType !== Constants.TRANSFORM_TYPE_PASSTHROUGH) {
                        variableOptions.push(retrievedVariable.name);
                    }
                }
            } else if (!this.props.coordinateSystemName) {
                variableOptions.push(retrievedVariable.name);
            }
        });


        variableOptions = variableOptions.filter(variableName => {
            let excludeVariable = false;

            //only give applicable options base on transformation type overrides
            let transformationTypeDefault = null;
            if (this.props.coordinateSystemName) {
                transformationTypeDefault = this.props.process.variable_retrieval.coordinate_systems[this.props.coordinateSystemName].dimensions[this.props.dimensionName].data_transformation.transformation_type;
            }
            switch (this.props.parameterNames[0]) {
                case 'range':
                    if (transformationTypeDefault !== Constants.TRANSFORM_TYPE_AVERAGE) {
                        excludeVariable = false;
                        ProcessUtils.visitTransformationVariables(this.props.process, 'transformation_type', (variableObj) => {
                            if (variableObj.name === variableName && variableObj.dim === this.props.dimensionName && variableObj.value === Constants.TRANSFORM_TYPE_AVERAGE) {
                                excludeVariable = true;
                            }
                        });

                    } else {
                        excludeVariable = true;
                        ProcessUtils.visitTransformationVariables(this.props.process, 'transformation_type', (variableObj) => {
                            if (variableObj.name === variableName && variableObj.dim === this.props.dimensionName && variableObj.value !== Constants.TRANSFORM_TYPE_AVERAGE) {
                                excludeVariable = false;
                            }
                        });
                    }
                    break;
                case 'width':
                case 'std_bad_max':
                case 'std_ind_max':
                case 'goodfrac_bad_min':
                case 'goodfrac_ind_min':
                    if (transformationTypeDefault === Constants.TRANSFORM_TYPE_AUTO || transformationTypeDefault === Constants.TRANSFORM_TYPE_AVERAGE) {
                        excludeVariable = false;
                        ProcessUtils.visitTransformationVariables(this.props.process, 'transformation_type', (variableObj) => {
                            if (variableObj.name === variableName && variableObj.dim === this.props.dimensionName &&
                                (variableObj.value !== Constants.TRANSFORM_TYPE_AUTO && variableObj.value !== Constants.TRANSFORM_TYPE_AVERAGE)) {
                                excludeVariable = true;
                            }
                        });

                    } else {
                        excludeVariable = true;
                        ProcessUtils.visitTransformationVariables(this.props.process, 'transformation_type', (variableObj) => {
                            if (variableObj.name === variableName && variableObj.dim === this.props.dimensionName &&
                                (variableObj.value === Constants.TRANSFORM_TYPE_AUTO || variableObj.value === Constants.TRANSFORM_TYPE_AVERAGE)) {
                                excludeVariable = false;
                            }
                        });
                    }
                    break;
                default:
                    break;
            }

            // filter out as option if it's already in the table for the same dimension
            ProcessUtils.visitTransformationVariables(this.props.process, this.props.parameterNames[0], (variableObj) => {
                if (this.props.dimensionName) {
                    if (variableObj.name === variableName && variableObj.dim === this.props.dimensionName) {
                        excludeVariable = true;
                    }
                } else {
                    if (variableObj.name === variableName) {
                        excludeVariable = true;
                    }
                }
            });

            return !excludeVariable;
        });

        return Constants.convertArrayToSelectOptions(variableOptions);
    };

    getQCType = () => {
        return this.props.parameterNames[0] === 'qc_mask' ? 'bit' : 'integer';
    };

    onAdd = () => {
        const options = this.getVariableOptions();
        const validator = new ProcessValidator(this.props.process);
        const isInputValid = (newNames) => {
            return validator.isEntityNull(newNames, 'Retrieved variable');
        };

        //TODO make new base class for selecting multiple variables and using arrow like import dialog and include here instead
        // will need for non-wizard version so input datastream can be selected as well
        ComponentUtils.openNewEntityModal(
            'Retrieved Variable',
            (selectedVariableNames) => {
                const editor = ProcessUtils.getEditor(this.props.process);

                selectedVariableNames.forEach(retrievedVariableName => {
                    const inputDataset = ProcessUtils.getInputDatasetForVariable(this.props.process, retrievedVariableName);
                    const inputDatastreams = ProcessUtils.getInputDatastreamsForDataset(this.props.process, inputDataset);
                    inputDatastreams.forEach(inputDatastream => {
                        editor.addTransformationParameterVariable(this.props.parameterNames, this.props.coordinateSystemName, this.props.dimensionName, retrievedVariableName, inputDatastream);
                    });
                });
                editor.commit();
            },
            isInputValid,
            {
                options,
                newSelectionPrefix: 'Retrieved variable:',
                inputLabel: 'Retrieved variable',
                creatable: false,
                multi: true }
        );
    };

    onDelete = (selectedItems) => {
        const editor = ProcessUtils.getEditor(this.props.process);
        Object.keys(selectedItems).forEach(key => {
            const datastreamName = this.getDatastreamFromKey(key);
            const retrievedVariableName = this.getRetrievedVariableFromKey(key);

            editor.removeTransformationParameterVariable(this.props.parameterNames, this.props.coordinateSystemName, this.props.dimensionName, retrievedVariableName, datastreamName);
        });

        editor.commit();
        this.clearSelection();
    };

    onValueChange = (fieldPath, value, data) => {
        let parameterName = this.props.parameterNames[0];
        const inputDatastream = data.inputDatastream;
        const variableName = data.retrievedVariable;

        // if override table is handling more than one parameter, the parameter name needs to be passed though data object
        if (this.props.parameterNames.length > 1) {
            parameterName = data.parameterName;
        }

        const editor = ProcessUtils.getEditor(this.props.process);

        value = editor.getFormattedValue(value);
        if (parameterName === 'range' && value === 0) {
            // If value is 0, then change that to the default value of LENGTH_OF_PROCESSING_INTERVAL
            value = Constants.RANGE_DEFAULT;
        }
        editor.updateTransformationParameterOverride(parameterName, this.props.coordinateSystemName, this.props.dimensionName, inputDatastream, variableName, value);
        editor.commit();
    };

    onNumberListChange = (fieldPath, value, changeData) => {
        value = ProcessUtils.getQCInputValue(value);
        this.onValueChange(fieldPath, value, changeData);
    };

    getQCDescriptions = (values, datastreamName, variableName) => {
        const descriptions = ProcessUtils.getQCDescriptions(this.props.process, datastreamName, variableName);

        const descriptionsList = Object.keys(descriptions).filter(key => {
            return values.indexOf(key) > -1 || values.indexOf(parseInt(key, 10)) > -1;
        }).map(key => {
            const desc = descriptions[key];
            return (
                <tr key={key}>
                    <td className="qc-number">{ key }:</td>
                    <td>{desc}</td>
                </tr>
            );
        });

        return descriptionsList;
    };

    renderBadQC = (data) => {
        const datastreamName = data.inputDatastream;
        const variableName = data.retrievedVariable;

        const badValues = ProcessUtils.getQCBits(this.props.process, datastreamName, variableName, 'bad', this.getQCType()).map(v => {
            return Number(v);
        });

        if (badValues.length > 0) {
            const descriptionsList = this.getQCDescriptions(badValues, datastreamName, variableName);

            const message = (
                <SimpleMessage>
                    <label>Bad QC Numbers</label>
                    <table className={qcHelpMessageStyles}>
                        <tbody>
                            {descriptionsList}
                        </tbody>
                    </table>
                </SimpleMessage>
            );

            return (
                <Flexbox>
                    <InfoIcon message={message}/>
                    {badValues.join(', ')}
                </Flexbox>
            );
        }
    };

    /** used in both qc bad and qc mask parameters **/
    renderIndQC = (data) => {
        // User can't set this field, so it's read-only and set to the values from the explored data
        const datastreamName = data.inputDatastream;
        const variableName = data.retrievedVariable;
        const indValues = ProcessUtils.getQCBits(this.props.process, datastreamName, variableName, 'indeterminate', this.getQCType());

        if (indValues.length > 0) {
            const descriptionsList = this.getQCDescriptions(indValues, datastreamName, variableName);

            const message = (
                <SimpleMessage>
                    <label>Indeterminate QC Numbers</label>
                    <table className={qcHelpMessageStyles}>
                        <tbody>
                            {descriptionsList}
                        </tbody>
                    </table>
                </SimpleMessage>
            );

            return (
                <Flexbox>
                    <InfoIcon message={message}/>
                    {indValues.join(', ')}
                </Flexbox>
            );
        }
    };


    /** used in both qc bad and qc mask parameters **/
    renderBadQCList = (data) => {
        const values = this.getValue(data);

        return (
            <RIEInput
                value={values}
                change={this.onNumberListChange}
                fieldPath={this.getFieldPath(this.props.parameterNames[0], data)}
                onChangeData={data}
                processId={this.props.process.name}
                processErrors={this.props.processErrors}
                formDisplay
                isDisabled={!this.props.hasWritePermissions}
            />
        );
    };

    renderBadQCValue = (data) => {
        const values = this.getValue(data);
        return ProcessUtils.convertBitPositionsToInteger(values);
    };

    renderToolbar() {
        const disabledMessage = ComponentUtils.getDisabledMessage(this.props.process.deprecated);
        if (this.props.selectable) {
            return (
                <Toolbar selectedItems={this.getSelectedItems()}>
                    <ToolbarItem
                        title="Delete"
                        className="fa fa-times delete"
                        clickHandler={this.onDelete}
                        selectionDependent
                        enabled={this.props.hasWritePermissions}
                        disabledMessage={disabledMessage}
                    />
                    <ToolbarItem
                        title="New"
                        className="fa fa-plus new"
                        clickHandler={this.onAdd}
                        enabled={this.props.hasWritePermissions}
                        disabledMessage={disabledMessage}
                    />
                </Toolbar>
            );
        } else {
            return <div className={css`margin-top:15px;`}/>;
        }
    }

    renderEmptyDataMsg() {
        const spanCount = this.getTableColumns().length + 1;
        return <tbody><tr><td colSpan={spanCount} style={{ textAlign: 'center' }}><i>No variables selected</i></td></tr></tbody>;
    }

    getAdditionalColumns() {
        // child should extend this to add parameter specific columns
        // should return array with column object
        return null;
    }

    getTableColumns() {
        const datastreamColumn = {
            property: 'inputDatastream',
            header: {
                label: 'Input Datastream',
                props: {
                    style: {
                        whiteSpace: 'nowrap',
                    }
                }
            }
        };
        const retrievedVariableColumn = {
            property: 'retrievedVariable',
            header: {
                label: 'Retrieved Variable',
                props: {
                    style: {
                        whiteSpace: 'nowrap'
                    }
                }
            }
        };

        let columns = [retrievedVariableColumn];
        if (this.props.displayInputDatastream) {
            columns = [datastreamColumn, ...columns];
        }

        if (this.getAdditionalColumns()) {
            columns = [...columns, ...this.getAdditionalColumns()];
        }

        return columns;
    }
}



// WEBPACK FOOTER //
// ./src/components/core/fields/transformation_parameters/OverrideTable.js